/**
 * Phase 8 chunk 7 — `<button data-geolocation>` progressive
 * enhancement for the /text/satellite §Visibility observer form.
 *
 * Without JS the button is inert (its `type="button"` keeps it from
 * submitting the form). With JS it:
 *   - Asks `navigator.geolocation.getCurrentPosition` for a fix
 *   - Fills the lat/lon/alt inputs in the closest `[data-observer-form]`
 *   - Re-submits the form so the page reloads with `?lat=&lon=&alt=`
 *
 * Bundle size: ~0.3 KB gzipped. Cesium-free; doesn't load anywhere
 * other than `/text/satellite/{norad}`.
 */

function fillForm(form: HTMLFormElement, position: GeolocationPosition): void {
  const lat = form.querySelector<HTMLInputElement>('input[name="lat"]');
  const lon = form.querySelector<HTMLInputElement>('input[name="lon"]');
  const alt = form.querySelector<HTMLInputElement>('input[name="alt"]');
  if (lat !== null) lat.value = position.coords.latitude.toFixed(4);
  if (lon !== null) lon.value = position.coords.longitude.toFixed(4);
  if (alt !== null) {
    const altitude = position.coords.altitude;
    alt.value = altitude === null || !Number.isFinite(altitude) ? '0' : altitude.toFixed(0);
  }
}

function setStatus(button: HTMLButtonElement, label: string): void {
  button.disabled = true;
  button.dataset.originalLabel ??= button.textContent ?? '';
  button.textContent = label;
}

function restoreStatus(button: HTMLButtonElement): void {
  button.disabled = false;
  if (button.dataset.originalLabel !== undefined) {
    button.textContent = button.dataset.originalLabel;
    delete button.dataset.originalLabel;
  }
}

function handleClick(event: Event): void {
  const target = event.target as HTMLElement | null;
  if (target === null) return;
  const button = target.closest('[data-geolocation]') as HTMLButtonElement | null;
  if (button === null) return;
  event.preventDefault();
  if (typeof navigator === 'undefined' || navigator.geolocation === undefined) {
    setStatus(button, 'Geolocation unsupported');
    return;
  }
  const form = button.closest('form[data-observer-form]') as HTMLFormElement | null;
  if (form === null) return;
  setStatus(button, '📍 Locating…');
  navigator.geolocation.getCurrentPosition(
    (position) => {
      fillForm(form, position);
      form.submit();
    },
    (err) => {
      const msg = err.code === err.PERMISSION_DENIED
        ? 'Permission denied'
        : err.code === err.POSITION_UNAVAILABLE
          ? 'Location unavailable'
          : err.code === err.TIMEOUT
            ? 'Timed out'
            : 'Location error';
      setStatus(button, `📍 ${msg}`);
      setTimeout(() => restoreStatus(button), 3000);
    },
    { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 },
  );
}

export function attachGeolocationFill(): void {
  if (typeof document === 'undefined') return;
  document.addEventListener('click', handleClick);
}

if (typeof document !== 'undefined') {
  attachGeolocationFill();
}
